import type { CustomTemplateBox } from '@lite-workspace/dto-fotoclass-templates';
import { SpreadData, SpreadRenderData } from '../interfaces/spread-data.interface';
import { BookData } from '../interfaces/book-data.interface';
import { CoverPrintSizes, PrintSizes } from '../interfaces/print-sizes.interface';
import type { BookGeometrySnapshot } from '../interfaces/book-geometry-snapshot.interface';
import { RectangleData } from '../interfaces/rectangle-data.interface';
import { isCoverPrintSizes } from './book-queries';
import { spreadPageOffset } from './spread-canvas-geometry';
import {
  DrawableBackgroundData,
  DrawableData,
  DrawableImageData,
  DrawableTextData,
  DrawableGroupData,
  DrawableVectorData,
  isDrawableImage,
  isDrawableText,
  isDrawableGroup,
  isDrawableVector,
} from '../interfaces/drawable-data.interface';
import { denormalizeSpread } from '../normalizer/denormalize';
import type { PersistedSpreadV1 } from '../normalizer/types';

export function getGapValue(gapPercent: number, height: number): number {
  return (gapPercent * height) / 100;
}

export function isCoverSpread(book: BookData, spread: SpreadData): boolean {
  return book.cover.id === spread.id;
}

/**
 * Spread-wide z-order is `(collage, order)`, never `order` alone. `order` is numbered from the drawable's
 * position INSIDE its own collage (the persist codec re-assigns it that way in `denormalize.ts`, and the
 * editor's own ops number 0..n per collage), so both collages of a spread start at 0 — sorting by `order`
 * alone interleaves them (0,0,1,1,2,2) instead of stacking the right page above the left one.
 */
function byCollageThenOrder(a: { collageIndex: number; order: number }, b: { collageIndex: number; order: number }): number {
  return a.collageIndex - b.collageIndex || a.order - b.order;
}

export function getAllDrawables(spread: SpreadData): DrawableData[] {
  const placed = (spread.collages ?? []).flatMap((collage, collageIndex) =>
    collage.drawableObjects.map(drawable => ({ collageIndex, order: drawable.order, drawable })),
  );
  // The drawables themselves are returned by reference — callers compare identity against the spread tree.
  return placed.sort(byCollageThenOrder).map(entry => entry.drawable);
}

/** Любая глубина: рамка внутри группы — такой же объект разворота, плоский обход её теряет. */
export function findDrawableById(spread: SpreadData, boxId: string): DrawableData | undefined {
  return findInDrawables(getAllDrawables(spread), boxId);
}

function findInDrawables(list: readonly DrawableData[], boxId: string): DrawableData | undefined {
  for (const d of list) {
    if (d.id === boxId) return d;
    if (d.type === 'group') {
      const nested = findInDrawables(d.drawableObjects, boxId);
      if (nested) return nested;
    }
  }

  return undefined;
}

export function isTemplateSpread(spread: SpreadData): boolean {
  return spread.collages.some(c => c.drawableObjects.some(d => d.isTemplateObject));
}

export function isSpreadEmpty(spread: SpreadData): boolean {
  return !spread.collages?.some(c =>
    c.drawableObjects.some(d => (isDrawableImage(d) && !!d.image) || (isDrawableText(d) && !!d.text)),
  );
}

/**
 * The ONE cover rule of a book: `cover` with content and `cover_sizes` wider than 0. A product without a
 * cover carries size 0; an empty cover is no cover. `model` is the stored document, v0 or v1.
 */
export function bookHasCover(model: unknown): boolean {
  const book = model as Partial<Pick<BookData, 'cover' | 'cover_sizes'>> | null | undefined;
  const cover = book?.cover;
  if (!cover || !hasCoverSize(book)) return false;
  const v1 = cover.collages?.some(collage => 'boxes' in collage);
  return !isSpreadEmpty(v1 ? denormalizeSpread(cover as unknown as PersistedSpreadV1).model : cover);
}

type CoverSized = { readonly cover_sizes?: { readonly width: number } } | null | undefined;

/** The book prints a cover: `cover_sizes` wider than 0. A product without a cover carries size 0. */
export function hasCoverSize(model: CoverSized): boolean {
  return (model?.cover_sizes?.width ?? 0) > 0;
}

/** The book is on a «Без фотообложки» product (its own `cover_by_lab` mark, stamped by assembly and product moves). */
export function bookCoverByLab(model: { readonly cover_by_lab?: boolean } | null | undefined): boolean {
  return model?.cover_by_lab === true;
}

/**
 * The book states a 0-wide cover sheet: nothing to print or preview on the cover. Older catalog rows carry such a
 * sheet without the lab mark, so this is no «Без фотообложки» signal ({@link bookCoverByLab} is). A book with no
 * `cover_sizes` at all (an older model) says nothing about its cover and is not empty.
 */
export function emptyCoverSheet(model: CoverSized): boolean {
  const sizes = model?.cover_sizes;
  return !!sizes && !(sizes.width > 0);
}

/** The legacy photobook import stamps `model.legacy.import`; no other writer sets it. */
export function isLegacyImport(model: unknown): boolean {
  return (model as { legacy?: { import?: unknown } } | null | undefined)?.legacy?.import != null;
}

/** A migrated book whose legacy cover was an attached file: printed as the file itself, never rendered. */
export function isFileCover(model: unknown): boolean {
  return isLegacyImport(model) && (model as { cover?: { type?: unknown } }).cover?.type === 'file';
}

/**
 * Легаси печатал файл обложки как есть, без подрезки под размер разворота. Холст обложки — пиксели файла при dpi
 * книги, без полей и корешка: редактор показывает файл целиком. `null` — размера файла нет.
 */
export function fileCoverSizes({ width, height }: { width: number; height: number }, dpi: number): CoverPrintSizes | null {
  if (!(width > 0 && height > 0)) return null;
  return {
    dpi,
    width,
    height,
    safe_horizontal: 0,
    safe_vertical: 0,
    cut_horizontal: 0,
    cut_vertical: 0,
    spine_width: 0,
    wrap_horizontal: 0,
    wrap_vertical: 0,
  };
}

/** Cover strip tile: `cover_sizes` wider than 0 on a catalog product, a printing cover or a legacy import (empty cover too). */
export function bookCoverTile(model: CoverSized, geometry: BookGeometrySnapshot | null | undefined): boolean {
  return hasCoverSize(model) && (geometry?.kind === 'catalog' || bookHasCover(model) || isLegacyImport(model));
}

/** The lab makes the cover (the product's «Без фотообложки» flag): no cover file printed, a placeholder in the editor. */
export function coverMadeByLab(geometry: BookGeometrySnapshot | null | undefined): boolean {
  return geometry?.kind === 'catalog' && geometry.dimensions.coverByLab === true;
}

/** The two cover variants a format may offer: the printed photo cover and the one the lab makes (fabric, leather). */
export const PHOTO_COVER_VARIANT_LABEL = 'Фотообложка';
export const LAB_COVER_VARIANT_LABEL = 'Обложка из ткани или кожи';

/** Подпись единственного разворота листа со сгибами (трюмо): страниц у него нет, номер не нужен. */
export const SOLE_SHEET_SPREAD_LABEL = 'Разворот';

export function coverVariantLabel(coverByLab: boolean | undefined): string {
  return coverByLab ? LAB_COVER_VARIANT_LABEL : PHOTO_COVER_VARIANT_LABEL;
}

/**
 * Products that are cover variants of each other: one product name, format and binding technology, one with a
 * photo cover and one without. Rows of the same format under other names are other products, not cover variants.
 */
export function coverPairKey(productName: string, formatName: string, technology: string | undefined): string {
  return `cover|${productName.trim().toLowerCase()}|${formatName.trim().toLowerCase()}|${technology ?? ''}`;
}

type CoverPairSide = {
  readonly productName: string;
  readonly formatName: string;
  readonly technology?: string;
  readonly coverByLab?: boolean;
};

/** The two rows are the photo cover and the lab cover of one product ({@link coverPairKey}). */
export function isCoverPair(a: CoverPairSide, b: CoverPairSide): boolean {
  return (
    (a.coverByLab === true) !== (b.coverByLab === true) &&
    coverPairKey(a.productName, a.formatName, a.technology) === coverPairKey(b.productName, b.formatName, b.technology)
  );
}

/** Product cover sizes a removed cover comes back with; `null` — nothing to add (no product cover, or it is there). */
export function coverSizesToAdd(model: CoverSized, geometry: BookGeometrySnapshot | null | undefined): CoverPrintSizes | null {
  if (geometry?.kind !== 'catalog' || !(geometry.dimensions.coverSizes.width > 0)) return null;
  return (model?.cover_sizes?.width ?? 0) === 0 ? geometry.dimensions.coverSizes : null;
}

// --- Collage horizontal offset ---

/**
 * Returns the render-space horizontal offset for a collage.
 * Left collage (index 0) always starts at 0.
 * Right collage (index > 0) starts at (W + S) / 2 = leftPageWidth + spine,
 * where S is the spine width (0 for non-cover spreads).
 */
export function getCollageHorizontalOffset(printSizes: PrintSizes, collageIndex: number): number {
  return spreadPageOffset(printSizes, collageIndex);
}

// --- Bleed offset ---

/**
 * Returns the bleed offset for a given PrintSizes.
 * For cover: uses wrap_horizontal / wrap_vertical.
 * For spread: uses cut_horizontal / cut_vertical.
 */
export function getBleedOffset(ps: PrintSizes): { x: number; y: number } {
  if (isCoverPrintSizes(ps)) {
    return { x: ps.wrap_horizontal ?? 0, y: ps.wrap_vertical ?? 0 };
  }

  return { x: ps.cut_horizontal, y: ps.cut_vertical };
}

// --- Spread ratio ---

export function getSpreadRatio(printSizes: PrintSizes): number {
  return printSizes.width / printSizes.height;
}

// --- Drawables with collage index ---

export type DrawableWithCollageIndex = DrawableData & { readonly collageIndex: number };

export function getAllDrawablesWithCollageIndex(spread: SpreadData): DrawableWithCollageIndex[] {
  const result: DrawableWithCollageIndex[] = [];
  for (const [collageIndex, collage] of (spread.collages ?? []).entries()) {
    for (const d of collage.drawableObjects) {
      result.push({ ...d, collageIndex });
    }
  }

  return result.sort(byCollageThenOrder);
}

// --- Hit testing ---

/** Result of a hit test against a spread's drawables. */
export interface HitTestResult {
  drawable: DrawableImageData;
  collageIndex: number;
  drawableIndex: number;
  /** The visual frame rect — group container if inside a group, otherwise undefined */
  frameContainer?: RectangleData;
}

/**
 * Pure hit-test: finds the topmost DrawableImageData at a point in full-spread model space.
 * Handles both top-level images and images nested inside groups.
 *
 * Callers are responsible for converting screen coordinates to model space:
 * - Desktop (Konva): `modelX = pointer.x / engine.scale` (Konva stage.offset handles bleed)
 * - Mobile: `modelX = tapX / scale + bleed.x`
 *
 * @param modelX - X coordinate in full-spread model space (with bleed)
 * @param modelY - Y coordinate in full-spread model space (with bleed)
 * @param spread - The spread data to test against
 */
export function findDrawableAtPoint(modelX: number, modelY: number, spread: SpreadRenderData): HitTestResult | null {
  const ps = spread.printSizes;

  for (let ci = 0; ci < spread.collages.length; ci++) {
    const collage = spread.collages[ci];
    const margin = getCollageHorizontalOffset(ps, ci);

    // Iterate in reverse to hit top-most elements first (highest z-order)
    for (let di = collage.drawableObjects.length - 1; di >= 0; di--) {
      const draw = collage.drawableObjects[di];

      if (draw.type === 'image') {
        const img = draw as DrawableImageData;
        const c = img.naturalAligns?.container;
        if (!c) continue;
        const left = c.left + margin;
        if (modelX >= left && modelX <= left + c.width && modelY >= c.top && modelY <= c.top + c.height) {
          return { drawable: img, collageIndex: ci, drawableIndex: di };
        }
      }

      if (draw.type === 'group') {
        const group = draw as DrawableGroupData;
        const gc = group.naturalAligns?.container;
        if (!gc) continue;
        const gLeft = gc.left + margin;
        if (modelX >= gLeft && modelX <= gLeft + gc.width && modelY >= gc.top && modelY <= gc.top + gc.height) {
          // Find the topmost image inside the group
          for (let ni = group.drawableObjects.length - 1; ni >= 0; ni--) {
            const nested = group.drawableObjects[ni];
            if (nested.type === 'image') {
              return {
                drawable: nested as DrawableImageData,
                collageIndex: ci,
                drawableIndex: di,
                frameContainer: gc,
              };
            }
          }
        }
      }
    }
  }

  return null;
}

/** Result of a text hit test against a spread's drawables. */
export interface TextHitTestResult {
  drawable: DrawableTextData;
  collageIndex: number;
  drawableIndex: number;
}

/**
 * Finds the topmost text drawable at a point in full-spread model space.
 * Only top-level drawables — text nested in groups is skipped so a tap on a photo
 * frame caption does not steal the hit from the photo. Coordinates match
 * `findDrawableAtPoint` (model space with bleed). Type-based filtering (which text
 * is editable) is the caller's responsibility.
 */
export function findTextDrawableAtPoint(modelX: number, modelY: number, spread: SpreadRenderData): TextHitTestResult | null {
  const ps = spread.printSizes;

  for (let ci = 0; ci < spread.collages.length; ci++) {
    const collage = spread.collages[ci];
    const margin = getCollageHorizontalOffset(ps, ci);

    for (let di = collage.drawableObjects.length - 1; di >= 0; di--) {
      const draw = collage.drawableObjects[di];
      if (!isDrawableText(draw)) continue;

      const c = draw.naturalAligns?.container;
      if (!c) continue;
      const left = c.left + margin;
      if (modelX >= left && modelX <= left + c.width && modelY >= c.top && modelY <= c.top + c.height) {
        return { drawable: draw, collageIndex: ci, drawableIndex: di };
      }
    }
  }

  return null;
}

/**
 * Типы drawable-объектов, которые участвуют в декоре спреда, но не являются
 * интерактивными целями для клика/drop/crop. Hit-test должен пропускать их и
 * искать интерактивный drawable (image/text/group) под ними. FKS-9245: без
 * этого декоративные ассеты design-pack'а «Городок 01» перехватывают drop-event
 * у фото-бокса на обложке.
 */
const DECORATIVE_DRAWABLE_TYPES: ReadonlySet<DrawableData['type']> = new Set(['background', 'object']);

/** Результат hit-test'а в screen-space с возможностью провалиться внутрь группы до image. */
export interface ScreenHitResult {
  drawable: DrawableData;
  /** Set when drawable was descended into via `descendIntoGroupsForImage` and originally lived in a group. */
  parentGroup?: DrawableGroupData;
}

export interface FindTopAtScreenOptions {
  /**
   * Когда top-hit оказался группой — провалиться в её `drawableObjects`,
   * найти image под точкой и вернуть его вместе с `parentGroup`.
   * Нужно drop/click handlers'ам, которые работают с конкретной image-рамкой
   * внутри группы (на DEFAULT-разворотах группа — это фрейм с image+text+декором,
   * а не атомарный target).
   */
  descendIntoGroupsForImage?: boolean;
}

/**
 * Hit-test указателя мыши/тача на canvas: возвращает верхний по z-order drawable,
 * под которым находится точка экрана.
 *
 * Учитывает `viewportOffset` Konva Stage — когда на обложке скрыты зоны bleed,
 * stage смещается так, что экран показывает содержимое с (bleed.x, bleed.y) в
 * model-координатах. Формула: `model = screen / scale + viewportOffset`.
 *
 * Mobile и desktop используют единую функцию, чтобы хит-тест визуально совпадал
 * с тем, что видит пользователь. Регрессия FKS-9081: на обложке клик по верхней
 * визуальной части контейнера не активировал crop, потому что desktop считал
 * координаты без смещения.
 *
 * При `options.descendIntoGroupsForImage = true` и попадании в группу — пытается
 * найти конкретную image-рамку внутри неё (children коорд-system — local
 * относительно group container). Возвращает image + parentGroup. Если image
 * под pointer'ом нет — отдаёт саму группу.
 */
export function findTopHitAtScreen(
  screenPointer: { x: number; y: number },
  scale: number,
  viewportOffset: { x: number; y: number },
  spread: SpreadRenderData,
  options?: FindTopAtScreenOptions,
): ScreenHitResult | undefined {
  const modelX = screenPointer.x / scale + viewportOffset.x;
  const modelY = screenPointer.y / scale + viewportOffset.y;

  const drawablesAtPointer = getAllDrawablesWithCollageIndex(spread).filter(draw => {
    const margin = getCollageHorizontalOffset(spread.printSizes, draw.collageIndex);
    const container = draw.naturalAligns.container;

    const left = margin + container.left;
    const top = container.top;
    const right = left + container.width;
    const bottom = top + container.height;

    return modelX >= left && modelX <= right && modelY >= top && modelY <= bottom;
  });

  if (spread.background) {
    const c = spread.background.naturalAligns.container;
    const insideBg = modelX >= c.left && modelX <= c.left + c.width && modelY >= c.top && modelY <= c.top + c.height;
    if (insideBg) {
      drawablesAtPointer.unshift({ ...spread.background, collageIndex: 0 });
    }
  }

  const interactive = drawablesAtPointer.filter(d => !DECORATIVE_DRAWABLE_TYPES.has(d.type));
  const top = interactive[interactive.length - 1] ?? drawablesAtPointer[drawablesAtPointer.length - 1];
  if (!top) return undefined;

  if (options?.descendIntoGroupsForImage && top.type === 'group') {
    const group = top as DrawableGroupData & { collageIndex: number };
    const margin = getCollageHorizontalOffset(spread.printSizes, group.collageIndex);
    const gc = group.naturalAligns.container;
    const localX = modelX - margin - gc.left;
    const localY = modelY - gc.top;

    for (let i = group.drawableObjects.length - 1; i >= 0; i--) {
      const child = group.drawableObjects[i];
      if (child.type !== 'image') continue;
      const cc = child.naturalAligns.container;
      if (localX >= cc.left && localX <= cc.left + cc.width && localY >= cc.top && localY <= cc.top + cc.height) {
        return { drawable: child, parentGroup: group };
      }
    }
  }

  return { drawable: top };
}

/**
 * Обёртка над {@link findTopHitAtScreen} для callsite'ов, которым достаточно
 * самого drawable без parentGroup. Поведение для группы — возвращает группу,
 * без descend.
 */
export function findTopDrawableAtScreen(
  screenPointer: { x: number; y: number },
  scale: number,
  viewportOffset: { x: number; y: number },
  spread: SpreadRenderData,
): DrawableData | undefined {
  return findTopHitAtScreen(screenPointer, scale, viewportOffset, spread)?.drawable;
}

/**
 * Padding хит-зоны текста в screen-px для редактора фотографа: мелкие ФИО на
 * виньетке тяжело попасть курсором. Расширяет видимую хит-зону текста
 * (`visibleTextHitRect`) при выборе подписи и при различении «клик по имени vs
 * по фото» в group-renderer.
 */
export const TEXT_HIT_PADDING_PX = 6;

// --- Hash functions ---

function rectangleRound(r: RectangleData): { left: number; top: number; width: number; height: number; angle: number } {
  return {
    left: Math.round(r.left),
    top: Math.round(r.top),
    width: Math.round(r.width),
    height: Math.round(r.height),
    angle: Math.round(r.angle),
  };
}

export function getDrawableDataHash(d: DrawableData): string {
  if (isDrawableImage(d)) {
    return getDrawableImageHash(d);
  }

  if (isDrawableText(d)) {
    return getDrawableTextHash(d);
  }

  if (isDrawableGroup(d)) {
    return getDrawableGroupHash(d);
  }

  if (isDrawableVector(d)) {
    return getDrawableVectorHash(d);
  }

  // background, object
  return d.id;
}

function getDrawableVectorHash(d: DrawableVectorData): string {
  const container = rectangleRound(d.naturalAligns.container);
  return (
    `${d.id}-` +
    `${container.left}-${container.top}-${container.width}-${container.height}-${container.angle}-` +
    `${d.rotation ?? 0}-${d.opacity ?? 1}-${d.blendMode ?? 'normal'}-${d.visible ?? true}-` +
    `${JSON.stringify(d.vector.fill ?? null)}-${JSON.stringify(d.vector.stroke ?? null)}-${d.vector.fillOpacity ?? 1}`
  );
}

function getDrawableImageHash(d: DrawableImageData): string {
  const image = rectangleRound((d.naturalAligns as { container: RectangleData; image: RectangleData }).image);
  const container = rectangleRound(d.naturalAligns.container);
  return (
    `${d.image?.id}-` +
    // Угол — сплит двух полей, persist-кодек v1 переносит его целиком в `d.rotation`; хэш без него слеп к повороту.
    `${container.left}-${container.top}-${container.width}-${container.height}-${container.angle}-${d.rotation ?? 0}-` +
    `${image.left}-${image.top}-${image.width}-${image.height}-${image.angle}-` +
    `${d.flipHorizontal ? 1 : 0}${d.flipVertical ? 1 : 0}`
  );
}

function getDrawableTextHash(d: DrawableTextData): string {
  const container = rectangleRound(d.naturalAligns.container);
  return (
    // eslint-disable-next-line max-len
    `${d.text.text}-${d.text.color}-${Math.round(d.text.letterSpacing)}-${Math.round(d.text.lineHeight)}-${d.text.fontName}-${d.text.fontWeight}-${d.text.italic}-${d.text.align}-` +
    `${d.naturalAligns.fontSize}-${d.textMode ?? ''}-${d.autoFit ?? ''}-${d.verticalAlign ?? ''}-` +
    `${container.left}-${container.top}-${container.width}-${container.height}-${container.angle}-${d.rotation ?? 0}-`
  );
}

function getDrawableGroupHash(d: DrawableGroupData): string {
  // eslint-disable-next-line max-len
  return `${d.id}-${d.params.personId}-${d.params.peopleType}-${d.drawableObjects.map(dro => getDrawableDataHash(dro)).join('-')}`;
}

export function getSpreadDataHash(spread: SpreadData, printSizes: PrintSizes): string {
  let dataForHash = `${printSizes.width}-${printSizes.height}-${printSizes.dpi}-`;
  if (spread.background) {
    const c = rectangleRound(spread.background.naturalAligns.container);
    dataForHash += `bg:${spread.background.data.fileId}-${c.left}-${c.top}-${c.width}-${c.height}-`;
  }

  spread.collages?.forEach(c => c.drawableObjects?.forEach(d => (dataForHash += getDrawableDataHash(d) + '-')));
  return dataForHash;
}

// --- Template page split ---

export interface SplitDrawablesByPageCenterResult<T extends DrawableData = DrawableData> {
  readonly left: readonly T[];
  readonly right: readonly T[];
  readonly crossing: readonly T[];
}

/** Horizontal extent of one item — all the split reads off a form; the rest of the shape is none of its business. */
interface PageSpan {
  readonly left: number;
  readonly width: number;
}

interface PageCenterBuckets<T> {
  readonly left: readonly T[];
  readonly right: readonly T[];
  readonly crossing: readonly T[];
}

function bucketByPageCenter<T>(items: readonly T[], midX: number, spanOf: (item: T) => PageSpan): PageCenterBuckets<T> {
  const left: T[] = [];
  const right: T[] = [];
  const crossing: T[] = [];

  for (const item of items) {
    const span = spanOf(item);
    const itemRight = span.left + span.width;

    if (span.left < midX && itemRight > midX) {
      crossing.push(item);
      continue;
    }

    const centerX = span.left + span.width / 2;
    if (centerX < midX) {
      left.push(item);
    } else {
      right.push(item);
    }
  }

  return { left, right, crossing };
}

/**
 * Routes drawables into left/right/crossing buckets based on container centerX
 * relative to the spread midline. Used during template→spread split:
 * - container straddles midX → crossing (range overrides centroid)
 * - centerX < midX → left page (collages[0])
 * - centerX >= midX → right page (collages[1])
 */
export function splitDrawablesByPageCenter<T extends DrawableData>(
  drawables: readonly T[],
  midX: number,
): SplitDrawablesByPageCenterResult<T> {
  return bucketByPageCenter(drawables, midX, d => d.naturalAligns.container);
}

// ─── v1-твин разреза по корешку (custom-template-v1, CustomTemplateBox) ─────────────────────
// Закон разреза общий с v0-ветвью выше — обе формы зовут `bucketByPageCenter`, расходится только
// адрес размаха: `naturalAligns.container` против `rect`. Две ветки — состояние перехода:
// v0-ветвь уйдёт вместе с последним вызывающим drawable-формы.

export type SplitBoxesByPageCenterResult<T extends CustomTemplateBox = CustomTemplateBox> = PageCenterBuckets<T>;

export function splitBoxesByPageCenter<T extends CustomTemplateBox>(
  boxes: readonly T[],
  midX: number,
): SplitBoxesByPageCenterResult<T> {
  return bucketByPageCenter(boxes, midX, box => ({ left: box.rect[0], width: box.rect[2] }));
}

// --- Background accessors ---

export function getSpreadBackground(spread: SpreadData): DrawableBackgroundData | undefined {
  return spread.background;
}

export function setSpreadBackground(spread: SpreadData, background: DrawableBackgroundData): SpreadData {
  return { ...spread, background };
}

export function removeSpreadBackground(spread: SpreadData): SpreadData {
  if (!spread.background) return spread;
  const { background: _omit, ...rest } = spread;
  return rest;
}
